import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TEConversionSubscription } from '@x/common/tracking/event-models/conversion.events';
import { TrackingService } from '@x/common/tracking/tracking.service';
import { CheckoutOrderStateModel } from '@x/ecommerce-shop/app/checkout/state/checkout-order.state';
import {
  RefreshSubscriber,
  SubscriberActivate,
  SubscriberFetch,
  SubscriberFetchChannelContext,
  SubscriberPause,
  SubscriberPaymentMethods,
  SubscriberReset,
  SubscriberTermsUrl,
  SubscriberUpdate,
  SubscriberUpdateAddress,
  SubscriberUpdateDeclaration,
  SubscriberUpdatePaymentMethod,
  SubscriberUpdateSet,
} from '@x/ecommerce-shop/app/subscriber-checkout/state/subscriber.actions';
import {
  IShopChannelCheckoutDetail,
  IShopPaymentMethod,
  IShopSubscriber,
  IShopSubscriberRequirement,
  ShopChannelService,
  ShopPaymentService,
  ShopSubscriberService,
  ShopSubscriptionService,
} from '@x/ecommerce/shop-client';
import { AddressAssignment } from '@x/schemas/ecommerce';
import { map, switchMap, tap } from 'rxjs/operators';

export interface SubscriberStateModel {
  subscriber: IShopSubscriber | null;
  channel: IShopChannelCheckoutDetail | null;
  subscriptionTermsUrl?: string | null | undefined;
  paymentMethods: IShopPaymentMethod[];
}

const defaults: SubscriberStateModel = {
  subscriber: null,
  channel: null,
  subscriptionTermsUrl: null,
  paymentMethods: [],
};

@State<SubscriberStateModel>({
  name: 'subscriberState',
  defaults,
})
@Injectable()
export class SubscriberState {
  constructor(
    private shopSubscriberService: ShopSubscriberService,
    private shopSubscriptionService: ShopSubscriptionService,
    private shopChannelService: ShopChannelService,
    private shopPaymentService: ShopPaymentService,
    @Inject(LOCALE_ID) private locale: string,
    private analyticsService: TrackingService,
  ) {}

  @Selector()
  static requirements(state: SubscriberStateModel): Array<IShopSubscriberRequirement> {
    return state.subscriber?.requirements?.requirements ?? [];
  }

  @Selector()
  static subscriber(state: SubscriberStateModel): IShopSubscriber | null {
    return state.subscriber;
  }

  @Selector()
  static subscriptionUrl(state: SubscriberStateModel): string | null | undefined {
    return state.subscriptionTermsUrl;
  }

  @Selector()
  static subscriberPaymentMethods(state: SubscriberStateModel): IShopPaymentMethod[] {
    return state.paymentMethods;
  }

  @Selector()
  static subscriberChannel(state: SubscriberStateModel): IShopChannelCheckoutDetail | null {
    return state.channel;
  }

  @Selector()
  static subscriberChannelRegionIds(state: CheckoutOrderStateModel): number[] {
    return state.channel?.regions.map((r) => r.id) ?? [];
  }

  @Action(RefreshSubscriber)
  refresh({ dispatch, getState, patchState }: StateContext<SubscriberStateModel>) {
    const subscriberId = getState().subscriber?.id;
    if (!subscriberId) return;
    return this.shopSubscriberService.fetchSubscriber({ subscriberId }).pipe(
      tap((subscriber) => patchState({ subscriber })),
      tap(() => {
        return dispatch([
          new SubscriberPaymentMethods(),
          new SubscriberFetchChannelContext(),
          new SubscriberTermsUrl(),
        ]);
      }),
    );
  }

  @Action(SubscriberFetch)
  fetch(
    { dispatch, patchState }: StateContext<SubscriberStateModel>,
    { subscriberId }: SubscriberFetch,
  ) {
    return this.shopSubscriberService.fetchSubscriber({ subscriberId }).pipe(
      tap((subscriber) => patchState({ subscriber })),
      switchMap(() => {
        return dispatch([
          new SubscriberPaymentMethods(),
          new SubscriberFetchChannelContext(),
          new SubscriberTermsUrl(),
        ]);
      }),
    );
  }

  @Action(SubscriberUpdateDeclaration)
  updateSubscriberDeclaration(
    { dispatch, getState }: StateContext<SubscriberStateModel>,
    { args }: SubscriberUpdateDeclaration,
  ) {
    const subscriberId = getState().subscriber?.id;

    if (!subscriberId) return;

    return this.shopSubscriberService
      .updateSubscriberDeclaration({ input: { subscriberId, ...args } })
      .pipe(tap(() => dispatch(new RefreshSubscriber())));
  }

  @Action(SubscriberUpdate)
  update({ dispatch, getState }: StateContext<SubscriberStateModel>) {
    const subscriberId = getState().subscriber?.id;

    if (!subscriberId) return;

    return this.shopSubscriberService
      .fetchSubscriber({ subscriberId })
      .pipe(switchMap((subscriber) => dispatch(new SubscriberUpdateSet(subscriber))));
  }

  @Action(SubscriberUpdateSet)
  set({ patchState }: StateContext<SubscriberStateModel>, { subscriber }: SubscriberUpdateSet) {
    patchState({
      subscriber,
    });
  }

  @Action(SubscriberFetchChannelContext)
  fetchChannelContext({ patchState, getState }: StateContext<SubscriberStateModel>) {
    const { subscriber } = getState();

    if (!subscriber) return;

    return this.shopChannelService
      .fetchChannelCheckoutDetail(subscriber.variantChannel, this.locale)
      .pipe(tap((channel) => patchState({ channel })));
  }

  @Action(SubscriberPaymentMethods)
  subscriberPaymentMethods({ patchState, getState }: StateContext<SubscriberStateModel>) {
    const { subscriber } = getState();

    if (!subscriber) return;

    return this.shopPaymentService
      .fetchPaymentMethods({ channelCode: subscriber.variantChannel })
      .pipe(
        map((paymentMethods) => paymentMethods.filter((m) => m.canReuse)),
        tap((paymentMethods) => {
          patchState({ paymentMethods });
        }),
      );
  }

  @Action(SubscriberTermsUrl)
  subscriberTermsUrl({ patchState, getState }: StateContext<SubscriberStateModel>) {
    const { subscriber } = getState();

    if (!subscriber) return;

    return this.shopSubscriptionService
      .fetchSubscriptionTermsUrl({
        id: subscriber.subscriptionId,
        locale: this.locale,
        channelCode: subscriber.variantChannel,
      })
      .pipe(
        tap((subscriptionTermsUrl) => {
          patchState({
            subscriptionTermsUrl,
          });
        }),
      );
  }

  @Action(SubscriberUpdatePaymentMethod)
  updatePaymentMethod(
    { getState, patchState, dispatch }: StateContext<SubscriberStateModel>,
    { userPaymentMethodId }: SubscriberUpdatePaymentMethod,
  ) {
    const subscriber = getState().subscriber;

    if (!subscriber) return;

    return this.shopSubscriberService
      .updateSubscriberPaymentMethod({
        input: {
          subscriberId: subscriber.id,
          userPaymentMethodId,
        },
      })
      .pipe(switchMap(() => dispatch(new SubscriberUpdate())));
  }

  @Action(SubscriberUpdateAddress)
  updateAddress(
    { getState, patchState, dispatch }: StateContext<SubscriberStateModel>,
    { addressInput }: SubscriberUpdateAddress,
  ) {
    const subscriber = getState().subscriber;

    if (!subscriber) return;

    return this.shopSubscriberService
      .updateSubscriberAddress({
        input: {
          subscriberId: subscriber.id,
          address: addressInput,
          assignment: AddressAssignment.Shipping,
        },
      })
      .pipe(switchMap(() => dispatch(new SubscriberUpdate())));
  }

  @Action(SubscriberActivate)
  activate({ getState, dispatch }: StateContext<SubscriberStateModel>) {
    const subscriber = getState().subscriber;

    if (!subscriber) return;

    return this.shopSubscriberService
      .activateSubscriber({ input: { subscriberId: subscriber.id } })
      .pipe(
        tap((subscriber) => {
          this.analyticsService.sendEvent(new TEConversionSubscription({ subscriber }));
        }),
        switchMap((subscriber) => dispatch(new SubscriberUpdateSet(subscriber))),
      );
  }

  @Action(SubscriberReset)
  reset({ setState }: StateContext<SubscriberStateModel>) {
    setState({
      subscriber: null,
      channel: null,
      paymentMethods: [],
    });
  }

  @Action(SubscriberPause)
  pause({ getState, dispatch }: StateContext<SubscriberStateModel>) {
    const subscriber = getState().subscriber;

    if (!subscriber) return;

    return this.shopSubscriberService
      .pauseSubscriber({ input: { subscriberId: subscriber.id } })
      .pipe(switchMap(() => dispatch(new SubscriberUpdate())));
  }
}
